import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  SlidersHorizontal, Play, Upload, Download, Calendar, ShieldCheck,
  TrendingUp, Lock, AlertTriangle, FileSpreadsheet, UserCheck, Users,
} from "lucide-react";
import { toast } from "sonner";

const todayMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function LeaveConfigMaster() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accrualMonth, setAccrualMonth] = useState(todayMonth());
  const [accrualRunning, setAccrualRunning] = useState(false);
  const [accrualResult, setAccrualResult] = useState(null);
  const [logRows, setLogRows] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [eligLoading, setEligLoading] = useState(false);
  const [bulkEnabling, setBulkEnabling] = useState(false);
  const [showUnenabled, setShowUnenabled] = useState(false);

  const loadEligibility = async () => {
    setEligLoading(true);
    try {
      const { data } = await api.get("/leave-config/eligibility-status");
      setEligibility(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eligibility load failed");
    } finally {
      setEligLoading(false);
    }
  };

  const bulkEnable = async () => {
    if (!eligibility) return;
    const cnt = eligibility.admin_staff.needs_enable_count;
    if (cnt === 0) {
      toast.info("All admin-staff already have EL & PL enabled");
      return;
    }
    if (!window.confirm(
      `Enable EL + PL eligibility for ${cnt} admin-staff employee(s)?\n\n` +
      `This sets el_applicable = true and pl_applicable = true on each row. ` +
      `Already-enabled rows are skipped. Audit-logged. ` +
      `After this, re-run the monthly accrual to credit them.`,
    )) return;
    setBulkEnabling(true);
    try {
      const { data } = await api.post("/leave-config/bulk-enable-eligibility", {
        enable_el: true, enable_pl: true,
      });
      toast.success(`Enabled ${data.modified} employee(s). Now run the monthly accrual.`);
      await loadEligibility();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk enable failed");
    } finally {
      setBulkEnabling(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leave-config");
      setCfg(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  const loadLog = async () => {
    setLogLoading(true);
    try {
      const params = { month: `${accrualMonth}-01` };
      const { data } = await api.get("/leave-config/accrual-log", { params });
      setLogRows(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Log load failed");
    } finally {
      setLogLoading(false);
    }
  };

  useEffect(() => { load(); loadEligibility(); }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const { data } = await api.put("/leave-config", {
        el_monthly_accrual: parseFloat(cfg.el_monthly_accrual),
        pl_monthly_accrual: parseFloat(cfg.pl_monthly_accrual),
        el_max_cap: parseFloat(cfg.el_max_cap),
        pl_max_cap: parseFloat(cfg.pl_max_cap),
        el_probation_block: !!cfg.el_probation_block,
        pl_probation_block: !!cfg.pl_probation_block,
        encashment_enabled: !!cfg.encashment_enabled,
        cron_enabled: !!cfg.cron_enabled,
      });
      setCfg(data);
      toast.success("Leave configuration saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runAccrual = async () => {
    if (!window.confirm(
      `Run monthly accrual for ${accrualMonth}? Existing employees with prior accrual for this month will be skipped (idempotent).`,
    )) return;
    setAccrualRunning(true);
    setAccrualResult(null);
    try {
      const { data } = await api.post("/leave-config/run-accrual", null, {
        params: { month: `${accrualMonth}-01` },
      });
      setAccrualResult(data);
      const msg = `Accrual complete — ${data.credited} credited`
        + (data.skipped_eligibility_off ? ` · ${data.skipped_eligibility_off} skipped (EL/PL toggle OFF)` : "")
        + (data.skipped_probation ? ` · ${data.skipped_probation} on probation` : "");
      if (data.skipped_eligibility_off > 0) {
        toast.warning(msg + " — see Rules tab to enable.");
      } else {
        toast.success(msg);
      }
      loadLog();
      loadEligibility();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Accrual failed");
    } finally {
      setAccrualRunning(false);
    }
  };

  const runFullFix = async () => {
    const clearProb = window.confirm(
      `Full Payroll Data Fix for ${accrualMonth}?\n\n` +
      "This will run 5 steps in sequence:\n" +
      "  1. Enable EL & PL for all admin-staff (skips already-enabled)\n" +
      "  2. Auto-clear probation for stuck admins (optional — click OK to include)\n" +
      "  3. Run monthly accrual for the month\n" +
      "  4. Refresh Leaves in attendance summary (committed uploads)\n" +
      "  5. Flag draft payroll runs as needing Recompute\n\n" +
      "OK = include probation clearance (step 2). Cancel = skip step 2 only.",
    );
    // For probation bypass one-shot on the accrual only, ask separately
    const includeInProb = window.confirm(
      "Also bypass probation for THIS accrual run only?\n\n" +
      "Yes: give EL/PL credit to admins still in probation window (retro credit).\n" +
      "No: keep probation gate active.",
    );
    setAccrualRunning(true);
    setAccrualResult(null);
    try {
      const { data } = await api.post("/leave-config/full-payroll-fix", {
        month: `${accrualMonth}-01`,
        enable_eligibility: true,
        clear_probation: clearProb,
        include_in_probation: includeInProb,
      });
      setAccrualResult(data);
      const s = data.steps || [];
      const step = (k) => s.find((x) => x.step === k) || {};
      const s1 = step("1_enable_eligibility");
      const s3 = step("3_monthly_accrual");
      const s4 = step("4_refresh_leaves");
      const s5 = step("5_mark_runs_stale");
      toast.success(
        `Full Fix done ✓ Eligibility+${s1.modified || 0} · Accrual credited ${s3.credited || 0} · Summaries refreshed ${s4.summaries_updated || 0} · Runs flagged ${s5.runs_flagged || 0}`,
        { duration: 8000 },
      );
      loadLog();
      loadEligibility();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Full Fix failed");
    } finally {
      setAccrualRunning(false);
    }
  };

  const resetAccrual = async () => {
    if (!window.confirm(
      `⚠️ Reset accrual for ${accrualMonth}?\n\nThis will:\n• Delete all auto-accrual log rows for this month\n• Reset auto-credited EL/PL to 0 in the ledger\n• Allow you to re-run after fixing eligibility\n\nManual overrides + used leaves are preserved.\n\nProceed?`,
    )) return;
    try {
      const { data } = await api.delete(`/leave-config/accrual-log/${accrualMonth}-01`);
      toast.success(`Reset done — ${data.log_deleted} log rows + ${data.ledger_reset} ledger rows cleared. Now click Run Now again.`);
      setAccrualResult(null);
      loadLog();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    }
  };

  if (loading || !cfg) {
    return (
      <div data-testid="leave-config-page" className="p-10 text-center text-slate-400">Loading…</div>
    );
  }

  return (
    <div data-testid="leave-config-page" className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <SlidersHorizontal className="h-7 w-7 text-slate-500" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase 5C</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Leave Config Master
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            EL & PL automation rules — monthly accrual rates, carry-forward caps, probation rules.
          </p>
        </div>
      </div>

      <Tabs defaultValue="rules" className="space-y-4">
        <TabsList data-testid="leave-config-tabs">
          <TabsTrigger value="rules" data-testid="tab-rules">Rules</TabsTrigger>
          <TabsTrigger value="accrual" data-testid="tab-accrual">Monthly Accrual</TabsTrigger>
          <TabsTrigger value="opening" data-testid="tab-opening">Opening Balances</TabsTrigger>
        </TabsList>

        {/* ===================== RULES TAB ===================== */}
        <TabsContent value="rules" className="space-y-4">
          {/* Eligibility coverage panel — surfaces employees missing the per-employee
              EL/PL toggle so HR can bulk-enable in one click. */}
          {eligibility && (
            <div className="bg-gradient-to-br from-red-50 via-white to-amber-50 border border-red-200 rounded-lg overflow-hidden" data-testid="eligibility-panel">
              <div className="px-4 py-3 border-b border-red-100 flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-red-100 flex items-center justify-center flex-shrink-0">
                  <UserCheck className="h-5 w-5 text-red-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-red-500 font-semibold">
                    Eligibility Coverage
                  </div>
                  <div className="text-base font-bold text-slate-900">
                    EL / PL Per-Employee Toggle Status
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    Accrual only credits employees whose <code className="text-[10px] bg-slate-100 px-1 rounded">el_applicable</code> & <code className="text-[10px] bg-slate-100 px-1 rounded">pl_applicable</code> flags are ON. Missing flags = silent skip — use this panel to spot &amp; fix.
                  </div>
                </div>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                <StatBox tone="pink" label="Admin Staff Total"
                          value={eligibility.admin_staff.total}
                          icon={<Users className="h-4 w-4" />}
                          testid="admin-total" />
                <StatBox tone="emerald" label="Both EL + PL Enabled"
                          value={eligibility.admin_staff.both_enabled}
                          icon={<UserCheck className="h-4 w-4" />}
                          testid="both-enabled" />
                <StatBox tone="amber" label="Needs Enable"
                          value={eligibility.admin_staff.needs_enable_count}
                          icon={<AlertTriangle className="h-4 w-4" />}
                          testid="needs-enable" />
                <div className="flex flex-col gap-2 justify-center">
                  <Button
                    onClick={bulkEnable}
                    disabled={bulkEnabling || eligibility.admin_staff.needs_enable_count === 0}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                    data-testid="bulk-enable-btn"
                  >
                    <UserCheck className="h-4 w-4 mr-1.5" />
                    {bulkEnabling ? "Enabling…" : `Enable for ${eligibility.admin_staff.needs_enable_count}`}
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={loadEligibility}
                    disabled={eligLoading}
                    className="text-[11px]"
                    data-testid="refresh-eligibility-btn"
                  >
                    {eligLoading ? "Refreshing…" : "Refresh"}
                  </Button>
                </div>
              </div>
              {eligibility.admin_staff.needs_enable_count > 0 && (
                <div className="px-4 pb-4">
                  <button
                    type="button"
                    onClick={() => setShowUnenabled((v) => !v)}
                    className="text-[11px] text-red-700 font-semibold hover:underline"
                    data-testid="toggle-unenabled-list"
                  >
                    {showUnenabled ? "▼ Hide" : "▶ Show"} list of {eligibility.unenabled_admin_staff.length} employees needing enable
                  </button>
                  {showUnenabled && (
                    <div className="mt-2 bg-white border border-slate-200 rounded-md max-h-64 overflow-y-auto">
                      <table className="w-full text-[11px]" data-testid="unenabled-list">
                        <thead className="bg-slate-50 text-slate-600 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-1.5 font-semibold">Name</th>
                            <th className="text-left px-3 py-1.5 font-semibold">Code</th>
                            <th className="text-left px-3 py-1.5 font-semibold">Punch</th>
                            <th className="text-left px-3 py-1.5 font-semibold">Category</th>
                            <th className="text-center px-3 py-1.5 font-semibold">EL</th>
                            <th className="text-center px-3 py-1.5 font-semibold">PL</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {eligibility.unenabled_admin_staff.map((e) => (
                            <tr key={e.id} className="hover:bg-red-50/30">
                              <td className="px-3 py-1.5 text-slate-900">{e.name}</td>
                              <td className="px-3 py-1.5 font-mono text-slate-600">{e.employee_code || "—"}</td>
                              <td className="px-3 py-1.5 font-mono text-slate-600">{e.punch_id || "—"}</td>
                              <td className="px-3 py-1.5 text-slate-600">{e.category}</td>
                              <td className="px-3 py-1.5 text-center">
                                {e.el_applicable ? <span className="text-emerald-600 font-bold">✓</span> : <span className="text-red-500">✗</span>}
                              </td>
                              <td className="px-3 py-1.5 text-center">
                                {e.pl_applicable ? <span className="text-emerald-600 font-bold">✓</span> : <span className="text-red-500">✗</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <RuleCard
              icon={<TrendingUp className="h-5 w-5 text-emerald-600" />}
              title="Monthly Accrual Rates"
              subtitle="Days credited to each eligible Working employee on the 1st of every month."
            >
              <div className="grid grid-cols-2 gap-3">
                <NumField
                  label="EL per month"
                  value={cfg.el_monthly_accrual}
                  testid="el-monthly-input"
                  onChange={(v) => setCfg({ ...cfg, el_monthly_accrual: v })}
                />
                <NumField
                  label="PL per month"
                  value={cfg.pl_monthly_accrual}
                  testid="pl-monthly-input"
                  onChange={(v) => setCfg({ ...cfg, pl_monthly_accrual: v })}
                />
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Bravura default: <strong>EL 1.25</strong> / <strong>PL 1.0</strong> per month.
              </p>
            </RuleCard>

            <RuleCard
              icon={<Lock className="h-5 w-5 text-amber-600" />}
              title="Carry-Forward Cap"
              subtitle="Maximum balance allowed. Accrual stops when cap is reached."
            >
              <div className="grid grid-cols-2 gap-3">
                <NumField
                  label="EL max cap"
                  value={cfg.el_max_cap}
                  testid="el-cap-input"
                  step="1"
                  onChange={(v) => setCfg({ ...cfg, el_max_cap: v })}
                />
                <NumField
                  label="PL max cap"
                  value={cfg.pl_max_cap}
                  testid="pl-cap-input"
                  step="1"
                  onChange={(v) => setCfg({ ...cfg, pl_max_cap: v })}
                />
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Bravura default: <strong>30</strong> days each.
              </p>
            </RuleCard>

            <RuleCard
              icon={<ShieldCheck className="h-5 w-5 text-red-600" />}
              title="Probation Block"
              subtitle="Employees on probation cannot apply for EL / PL."
            >
              <div className="space-y-3">
                <ToggleRow
                  label="Block EL during probation"
                  hint="EL apply requests are rejected with a bilingual error until probation completes."
                  value={!!cfg.el_probation_block}
                  testid="el-prob-block"
                  onChange={(v) => setCfg({ ...cfg, el_probation_block: v })}
                />
                <ToggleRow
                  label="Block PL during probation"
                  hint="Optional — Bravura keeps this OFF (PL active immediately)."
                  value={!!cfg.pl_probation_block}
                  testid="pl-prob-block"
                  onChange={(v) => setCfg({ ...cfg, pl_probation_block: v })}
                />
              </div>
            </RuleCard>

            <RuleCard
              icon={<Calendar className="h-5 w-5 text-violet-600" />}
              title="Automation"
              subtitle="Master switches for the monthly cron and encashment."
            >
              <div className="space-y-3">
                <ToggleRow
                  label="Auto-accrual cron"
                  hint="Runs 1st of every month @ 06:00 IST. Disable to manage balances manually."
                  value={!!cfg.cron_enabled}
                  testid="cron-enabled"
                  onChange={(v) => setCfg({ ...cfg, cron_enabled: v })}
                />
                <ToggleRow
                  label="Encashment enabled"
                  hint="Bravura policy: No encashment of unused EL/PL."
                  value={!!cfg.encashment_enabled}
                  testid="encashment-enabled"
                  onChange={(v) => setCfg({ ...cfg, encashment_enabled: v })}
                />
              </div>
            </RuleCard>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={save}
              disabled={saving}
              className="brand-bg brand-bg-hover text-white"
              data-testid="save-config-btn"
            >
              {saving ? "Saving…" : "Save Configuration"}
            </Button>
          </div>
        </TabsContent>

        {/* ===================== ACCRUAL TAB ===================== */}
        <TabsContent value="accrual" className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Play className="h-5 w-5 text-emerald-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">Manual Accrual Run</h3>
                <p className="text-xs text-slate-500">
                  The auto-cron runs on the 1st of every month @ 06:00 IST. Use this to back-fill
                  or trigger early. The job is <strong>idempotent</strong> — already-credited
                  employees for the same month are skipped.
                </p>
              </div>
            </div>
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Month</Label>
                <Input
                  type="month"
                  value={accrualMonth}
                  onChange={(e) => setAccrualMonth(e.target.value)}
                  className="w-44"
                  data-testid="accrual-month"
                />
              </div>
              <Button
                onClick={runAccrual}
                disabled={accrualRunning}
                className="brand-bg brand-bg-hover text-white"
                data-testid="run-accrual-btn"
              >
                {accrualRunning ? "Running…" : "Run Now"}
              </Button>
              <Button
                onClick={runFullFix}
                disabled={accrualRunning}
                className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
                data-testid="run-full-fix-btn"
                title="One-click: enable eligibility → clear probation (optional) → run accrual → refresh attendance leaves → mark payroll for recompute"
              >
                {accrualRunning ? "Running…" : "🛠 Full Payroll Fix"}
              </Button>
              <Button
                variant="outline"
                onClick={loadLog}
                disabled={logLoading}
                data-testid="load-log-btn"
              >
                {logLoading ? "Loading…" : "Refresh Log"}
              </Button>
              {isSuperAdmin && (
                <Button
                  variant="outline"
                  onClick={resetAccrual}
                  className="text-red-700 border-red-300 hover:bg-red-50"
                  data-testid="reset-accrual-btn"
                  title="Super-admin only · delete log + zero auto-accrued for this month"
                >
                  Reset & Re-run
                </Button>
              )}
            </div>
            {accrualResult && (
              <div data-testid="accrual-result" className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat label="Credited" value={accrualResult.credited} tone="emerald" />
                  <Stat label="Skipped (Probation)" value={accrualResult.skipped_probation} tone="amber" />
                  <Stat label="Skipped (Toggle OFF)" value={accrualResult.skipped_eligibility_off || 0} tone="rose" />
                  <Stat label="Capped at max" value={accrualResult.capped} tone="slate" />
                </div>
                {(accrualResult.skipped_eligibility_off || 0) > 0 && (
                  <div className="mt-2 bg-red-50 border border-red-200 rounded p-2 text-red-800 text-[11px]" data-testid="eligibility-warning">
                    <AlertTriangle className="inline h-3 w-3 mr-1" />
                    <strong>{accrualResult.skipped_eligibility_off}</strong> admin-staff skipped because their EL/PL toggle is OFF. Go to <strong>Rules tab</strong> → click <strong>"Enable for N"</strong> button, then click <strong>"Reset & Re-run"</strong> here.
                  </div>
                )}
                {accrualResult.errors?.length > 0 && (
                  <div className="text-red-700 mt-2">
                    <AlertTriangle className="inline h-3 w-3 mr-1" />
                    {accrualResult.errors.length} error(s)
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Accrual Log table */}
          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 text-sm">Accrual Log — {accrualMonth}</h3>
              <span className="text-xs text-slate-500">{logRows.length} row(s)</span>
            </div>
            <table className="w-full text-xs min-w-[700px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Employee</th>
                  <th className="text-right px-3 py-2 text-emerald-700">+ EL</th>
                  <th className="text-right px-3 py-2 text-emerald-700">+ PL</th>
                  <th className="text-right px-3 py-2">EL Opening</th>
                  <th className="text-right px-3 py-2">PL Opening</th>
                  <th className="text-left px-3 py-2">Probation</th>
                  <th className="text-left px-3 py-2">Run At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logRows.length === 0 && (
                  <tr><td colSpan="7" className="text-center py-8 text-slate-400">
                    No log entries for this month. Click "Run Now" or "Refresh Log".
                  </td></tr>
                )}
                {logRows.map((r) => (
                  <tr key={r.id} data-testid={`accrual-log-${r.id}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.employee_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{r.punch_id}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700">{(r.el_credited || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700">{(r.pl_credited || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono">{(r.el_opening || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono">{(r.pl_opening || 0).toFixed(2)}</td>
                    <td className="px-3 py-2">
                      {r.probation_done ? (
                        <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">Completed</span>
                      ) : (
                        <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded">In Probation</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[10px] text-slate-500">
                      {r.created_at ? new Date(r.created_at).toLocaleString("en-IN") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ===================== OPENING BALANCE TAB ===================== */}
        <TabsContent value="opening" className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
            <div className="flex items-start gap-3">
              <FileSpreadsheet className="h-5 w-5 text-violet-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">Opening Balance Upload</h3>
                <p className="text-xs text-slate-500">
                  Use this when migrating EL/PL balances from Google Sheets or a prior system.
                  Bulk via CSV is fastest. For single-entry corrections, edit directly on the{" "}
                  <a href="/leave-ledger" className="text-red-600 hover:underline">Leave Ledger</a> page.
                </p>
              </div>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await api.get("/leave-config/opening-balances/template", {
                      responseType: "blob",
                    });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", "opening_balances_template.csv");
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  } catch (e) {
                    toast.error("Template download failed");
                  }
                }}
                data-testid="download-template-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download CSV Template
              </Button>
              <Button
                className="brand-bg brand-bg-hover text-white"
                onClick={() => setBulkOpen(true)}
                data-testid="open-bulk-dialog"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload CSV
              </Button>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-[11px] text-slate-600">
              <strong>CSV format:</strong> Header row required. Columns —{" "}
              <code>punch_id, employee_code, opening_el, opening_pl</code>. Either{" "}
              <code>punch_id</code> OR <code>employee_code</code> can identify the employee.
              Decimal values supported (e.g., 1.25, 5.5).
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <BulkUploadDialog open={bulkOpen} onClose={() => setBulkOpen(false)} />
    </div>
  );
}

function RuleCard({ icon, title, subtitle, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
      <div className="flex items-start gap-3">
        {icon}
        <div>
          <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function NumField({ label, value, onChange, testid, step = "0.25" }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
      <Input
        type="number"
        step={step}
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}

function ToggleRow({ label, hint, value, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900">{label}</div>
        <div className="text-[11px] text-slate-500">{hint}</div>
      </div>
      <Switch checked={value} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

function Stat({ label, value, tone = "slate" }) {
  const toneCls = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    slate: "text-slate-700",
  }[tone] || "text-slate-700";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xl font-semibold font-mono ${toneCls}`}>{value}</div>
    </div>
  );
}

function StatBox({ label, value, tone = "slate", icon, testid }) {
  const tones = {
    pink: "bg-red-100 text-red-700 border-red-200",
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
  };
  const cls = tones[tone] || tones.slate;
  return (
    <div className={`rounded-md border ${cls} px-3 py-2 flex items-center gap-2`} data-testid={testid}>
      <div className="flex-shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider opacity-80 truncate">{label}</div>
        <div className="text-2xl font-bold font-mono leading-tight">{value}</div>
      </div>
    </div>
  );
}


function BulkUploadDialog({ open, onClose }) {
  const [month, setMonth] = useState(todayMonth());
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    if (!file) {
      toast.error("Choose a CSV file first");
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/leave-config/opening-balances/csv?month=${month}-01`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(data);
      toast.success(`${data.applied} row(s) applied · ${data.errors?.length || 0} error(s)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Opening Balance Upload</DialogTitle>
          <DialogDescription>
            CSV with columns: punch_id (or employee_code), opening_el, opening_pl.
            Balances are stored for the chosen month.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Month</Label>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-44"
              data-testid="bulk-month-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">CSV file</Label>
            <Input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="bulk-file-input"
            />
          </div>
          {result && (
            <div data-testid="bulk-result" className="bg-slate-50 border border-slate-200 rounded p-3 text-xs space-y-1">
              <div className="font-semibold text-emerald-700">{result.applied} applied</div>
              {result.errors?.length > 0 && (
                <details>
                  <summary className="cursor-pointer text-red-700">
                    {result.errors.length} error(s) — show
                  </summary>
                  <ul className="mt-1 text-red-700 space-y-0.5">
                    {result.errors.slice(0, 20).map((e, i) => (
                      <li key={i}>
                        {e.row.punch_id || e.row.employee_code || e.row.employee_id || "(no id)"}: {e.error}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button
            onClick={submit}
            disabled={uploading || !file}
            className="brand-bg brand-bg-hover text-white"
            data-testid="bulk-submit-btn"
          >
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
